import { useEffect, useState, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import UserCarousel from "./UserCarousel";
import SpeechBubble from "./SpeechBubble";
import Flag from "react-world-flags";
import { generateQuestion } from "../../utilities/generateQuestion";
import PublicProfileModal from "../../modals/publicProfileModal";
import BigButton from "../events/BigButton";
import WhatIsIcebreaker from "../../modals/whatIsIcebreaker";

const db = getFirestore();

export default function IceBreaker({ show, onClose, title, attendees }) {
	const [showProfileModal, setShowProfileModal] = useState(false);
	const [validUsers, setValidUsers] = useState([]);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [showInfoModal, setShowInfoModal] = useState(false);
	const openProfileModal = () => setShowProfileModal(true);
	const closeProfileModal = () => setShowProfileModal(false);

	useEffect(() => {
		if (!attendees || attendees.length === 0) return;

		async function fetchAttendees() {
			try {
				const users = await Promise.all(
					attendees.map(async (uid) => {
						const userDoc = await getDoc(doc(db, "users", uid));
						if (!userDoc.exists()) return null;
						const data = userDoc.data();
						return {
							uid,
							...data,
							language: data.language || "English",
							subject: data.subject || "userData",
						};
					}),
				);

				const filteredUsers = users.filter(Boolean);
				setValidUsers(filteredUsers);

				if (filteredUsers.length > 0) {
					setCurrentIndex(Math.floor(Math.random() * filteredUsers.length));
				}
			} catch (error) {
				console.error("Error fetching attendees:", error);
			}
		}

		fetchAttendees();
	}, [attendees]);

	const currentUser = validUsers[currentIndex] || null;

	// Next user
	const showNextUser = () => {
		if (validUsers.length > 1) {
			setCurrentIndex((prev) => (prev + 1) % validUsers.length);
		}
	};

	// Prev user
	const showPrevUser = () => {
		if (validUsers.length > 1) {
			setCurrentIndex((prev) => (prev - 1 + validUsers.length) % validUsers.length);
		}
	};

	// ✅ Stable question generator
	const onGenerateQuestion = useCallback((userData) => generateQuestion(userData), []);

	// ✅ Persist language change to Firestore
	const saveLanguage = useCallback(async (uid, lang) => {
		try {
			await updateDoc(doc(db, "users", uid), { language: lang });
			// ✅ update local state so it persists when switching users
			setValidUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, language: lang } : u)));
		} catch (err) {
			console.error("Failed to save language:", err);
		}
	}, []);

	const handleSubjectChange = useCallback(async (uid, subjectChosen) => {
		try {
			await updateDoc(doc(db, "users", uid), { subject: subjectChosen });
			// ✅ update local state so it persists when switching users
			setValidUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, subject: subjectChosen } : u)));
		} catch (err) {
			console.error("Failed to update subject:", err);
		}
	}, []);

	return (
		<>
			<Modal show={show} onHide={onClose} fullscreen>
				<div className="w-100">
					{/* Row 1: Go Back */}
					<div className="ms-3 d-flex gap-3">
						<BigButton
							title="Go Back"
							icon="bi bi-arrow-left-circle"
							onClick={onClose ? onClose : () => window.history.back()}
						/>
					</div>
					<div style={{ position: "absolute", top: "20px", right: "20px" }}>
						{currentUser && (
							<span
								className="ice-light d-flex align-items-center gap-1 fw-bold"
								style={{ cursor: "pointer", fontSize: "1.1rem" }}
								onClick={() => setShowInfoModal(true)}
							>
								<i className="bi bi-info-circle-fill text-info fs-4"></i>
								<span className="ms-1 mt-1">What's this?</span>
							</span>
						)}
					</div>

					{/* Row 2: Name + Flag + Info */}
					<div className="d-flex align-items-center justify-content-center gap-3">
						{currentUser?.nationality && (
							<Flag code={currentUser.nationality.code} style={{ width: "30px", height: "20px" }} />
						)}

						{currentUser ? (
							<span
								onClick={openProfileModal}
								className="fw-semibold text-primary text-decoration-underline"
								style={{ cursor: "pointer" }}
								title="View Profile"
							>
								{currentUser.displayName}
							</span>
						) : (
							<span>{title}</span>
						)}
					</div>
				</div>

				<div className="d-flex flex-column overflow-hidden" style={{ minHeight: "70vh" }}>
					<div className="w-100 mb-1">
						<UserCarousel users={validUsers} currentIndex={currentIndex} onSelectIndex={setCurrentIndex} />
					</div>

					<div className="d-flex justify-content-center w-100 my-2">
						<Button onClick={showPrevUser} className="me-3" variant="link">
							<i className="bi bi-caret-left-fill"></i>
						</Button>

						{currentUser?.photoURL ? (
							<img
								src={currentUser.photoURL}
								alt={currentUser.displayName}
								style={{
									maxHeight: "70vh",
									maxWidth: "70vw",
									objectFit: "contain",
									borderRadius: "10px",
								}}
							/>
						) : (
							<p>No photo available</p>
						)}

						<Button onClick={showNextUser} className="ms-3" variant="link">
							<i className="bi bi-caret-right-fill"></i>
						</Button>
					</div>

					{/* ✅ SpeechBubble with language support */}
					<div className="d-flex justify-content-center">
						{currentUser && (
							<SpeechBubble
								data={currentUser}
								initialLanguage={currentUser.language}
								initialSubject={currentUser.subject}
								onGenerateQuestion={onGenerateQuestion}
								onLanguageChange={(lang) => saveLanguage(currentUser.uid, lang)}
								onSubjectChange={(subjectChosen) => handleSubjectChange(currentUser.uid, subjectChosen)}
							/>
						)}
					</div>
				</div>
			</Modal>

			{/* Fullscreen Profile Modal */}
			<PublicProfileModal show={showProfileModal} onHide={closeProfileModal} currentUser={currentUser} />
			<WhatIsIcebreaker show={showInfoModal} onHide={() => setShowInfoModal(false)} name={currentUser?.displayName} />
		</>
	);
}
